// src/services/data/chartQueries.ts
/**
 * Chart Query Factories
 * 
 * Encapsulates fetching logic for each chart type.
 * Used by useChartData to orchestrate data fetching.
 */

import type { ChartSpec, MetricSpec as ChartMetricSpec, StatCardData, TreemapNode } from '@/types/assistant';
import {
  getMetricSpec,
  getXMetricSpec,
  getYMetricSpec,
  getSizeMetricSpec,
  getColorMetricSpec,
  isWindowedMetric,
} from '@/types/assistant';
import {
  fetchTokenCurrentValues,
  fetchTokenHistoricalData,
  fetchCategoryCurrentValues,
  fetchCategoryChangeValues,
  fetchCategoryHistoricalData,
  filterByTimeframe,
  filterAndNormalizeForComparison,
  parseTimeframeToDays,
  sortByValueDescending,
  type TimeSeriesData,
  type MetricSpec,
} from './fetchers';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface BarData {
  categories: string[];
  values: number[];
}

export interface ScatterData {
  points: Array<{
    x: number;
    y: number;
    label: string;
    size?: number;
    color?: number;
  }>;
}

export interface TreemapData {
  nodes: TreemapNode[];
}

export interface StatCardDataResult {
  stat: StatCardData;
}

export type ChartData =
  | { type: 'historical' | 'multi_historical'; data: TimeSeriesData }
  | { type: 'time_series'; data: TimeSeriesData }
  | { type: 'comparison'; data: TimeSeriesData }
  | { type: 'bar' | 'distribution'; data: BarData }
  | { type: 'scatter'; data: ScatterData }
  | { type: 'treemap'; data: TreemapData }
  | { type: 'stat_card'; data: StatCardDataResult };

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const isCategoryChart = (spec: ChartSpec) => !!(spec.categories?.length);

function toFetcherMetricSpec(chartMetric: ChartMetricSpec | undefined): MetricSpec | undefined {
  if (!chartMetric) return undefined;
  
  // Build result with only defined properties (exactOptionalPropertyTypes)
  const result: MetricSpec = { key: chartMetric.key };
  
  if (isWindowedMetric(chartMetric)) {
    if (chartMetric.transform) {
      result.transform = chartMetric.transform;
    }
    if (chartMetric.window) {
      result.window = chartMetric.window;
    }
  }
  
  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
// Chart Type Fetchers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch historical time series data.
 * Supports both token and category charts with optional normalization.
 */
export async function fetchHistoricalChartData(
  spec: ChartSpec,
  normalize = false
): Promise<TimeSeriesData> {
  const timeframeDays = parseTimeframeToDays(spec.timeframe);
  const metricSpec = getMetricSpec(spec);
  if (!metricSpec) return { dates: [], series: [] };

  let data: TimeSeriesData;

  if (isCategoryChart(spec)) {
    data = await fetchCategoryHistoricalData(spec.categories!, metricSpec.key);
  } else if (spec.tickers?.length) {
    data = await fetchTokenHistoricalData(spec.tickers, metricSpec.key);
  } else {
    return { dates: [], series: [] };
  }

  return normalize
    ? filterAndNormalizeForComparison(data.dates, data.series, timeframeDays)
    : filterByTimeframe(data.dates, data.series, timeframeDays);
}

/**
 * Fetch bar chart data (current values).
 */
export async function fetchBarChartData(spec: ChartSpec): Promise<BarData> {
  const chartMetricSpec = getMetricSpec(spec);
  if (!chartMetricSpec) return { categories: [], values: [] };

  let valuesMap: Record<string, number>;
  let entities: string[];

  if (isCategoryChart(spec)) {
    entities = spec.categories!;
    valuesMap = await fetchCategoryCurrentValues(entities, chartMetricSpec.key);
  } else if (spec.tickers?.length) {
    entities = spec.tickers;
    const fetcherSpec = toFetcherMetricSpec(chartMetricSpec);
    if (!fetcherSpec) return { categories: [], values: [] };
    valuesMap = await fetchTokenCurrentValues(spec.tickers, fetcherSpec);
  } else {
    return { categories: [], values: [] };
  }

  // Filter out zero/null values
  const validEntities = entities.filter(e => valuesMap[e] && valuesMap[e] !== 0);

  if (validEntities.length === 0) return { categories: [], values: [] };

  return {
    categories: validEntities,
    values: validEntities.map(e => valuesMap[e] ?? 0),
  };
}

/**
 * Fetch scatter chart data (x, y, optional size and color).
 */
export async function fetchScatterChartData(spec: ChartSpec): Promise<ScatterData> {
  const xMetricSpec = getXMetricSpec(spec);
  const yMetricSpec = getYMetricSpec(spec);
  const sizeMetricSpec = getSizeMetricSpec(spec);
  const colorMetricSpec = getColorMetricSpec(spec);

  if (!spec.tickers?.length || !xMetricSpec || !yMetricSpec) {
    return { points: [] };
  }

  const xFetcher = toFetcherMetricSpec(xMetricSpec);
  const yFetcher = toFetcherMetricSpec(yMetricSpec);
  const sizeFetcher = toFetcherMetricSpec(sizeMetricSpec);
  const colorFetcher = toFetcherMetricSpec(colorMetricSpec);

  if (!xFetcher || !yFetcher) return { points: [] };

  // Fetch all metrics in parallel
  const [xValues, yValues, sizeValues, colorValues] = await Promise.all([
    fetchTokenCurrentValues(spec.tickers, xFetcher),
    fetchTokenCurrentValues(spec.tickers, yFetcher),
    sizeFetcher
      ? fetchTokenCurrentValues(spec.tickers, sizeFetcher)
      : Promise.resolve({} as Record<string, number>),
    colorFetcher
      ? fetchTokenCurrentValues(spec.tickers, colorFetcher)
      : Promise.resolve({} as Record<string, number>),
  ]);

  const points = spec.tickers.map(ticker => {
    const point: ScatterData['points'][number] = {
      x: xValues[ticker] ?? 0,
      y: yValues[ticker] ?? 0,
      label: ticker,
    };
    if (sizeFetcher && sizeValues[ticker] !== undefined) {
      point.size = sizeValues[ticker];
    }
    if (colorFetcher && colorValues[ticker] !== undefined) {
      point.color = colorValues[ticker];
    }
    return point;
  });

  return { points };
}

/**
 * Fetch treemap chart data (size metric, optional color metric).
 */
export async function fetchTreemapChartData(spec: ChartSpec): Promise<TreemapData> {
  // Pre-computed nodes take precedence
  if (spec.nodes?.length) {
    return { nodes: spec.nodes };
  }

  const sizeMetricSpec = getSizeMetricSpec(spec) ?? getMetricSpec(spec);
  const rawColorMetricSpec = getColorMetricSpec(spec);

  // Normalize color metric to change transform
  const colorMetricSpec: MetricSpec | undefined = rawColorMetricSpec
    ? {
        key: rawColorMetricSpec.key,
        transform: 'change',
        window: rawColorMetricSpec.window ?? '7d',
      }
    : undefined;

  if (!sizeMetricSpec) return { nodes: [] };

  const nodes: TreemapNode[] = [];

  if (spec.tickers?.length) {
    // Token treemap
    const sizeFetcher: MetricSpec = { key: sizeMetricSpec.key };
    
    const [sizeValues, colorValues] = await Promise.all([
      fetchTokenCurrentValues(spec.tickers, sizeFetcher),
      colorMetricSpec
        ? fetchTokenCurrentValues(spec.tickers, colorMetricSpec)
        : Promise.resolve({} as Record<string, number>),
    ]);

    for (const ticker of spec.tickers) {
      const sizeValue = sizeValues[ticker] ?? 0;
      if (sizeValue > 0) {
        const node: TreemapNode = { name: ticker, value: sizeValue };
        const colorVal = colorValues[ticker];
        if (colorVal !== undefined) node.color_value = colorVal;
        nodes.push(node);
      }
    }
  } else if (spec.categories?.length) {
    // Category treemap
    const [sizeValues, colorValues] = await Promise.all([
      fetchCategoryCurrentValues(spec.categories, sizeMetricSpec.key),
      colorMetricSpec
        ? fetchCategoryChangeValues(spec.categories, colorMetricSpec.key, colorMetricSpec.window ?? '7d')
        : Promise.resolve({} as Record<string, number>),
    ]);

    for (const catCode of spec.categories) {
      const sizeValue = sizeValues[catCode] ?? 0;
      if (sizeValue > 0) {
        const node: TreemapNode = { name: catCode, value: sizeValue };
        const colorVal = colorValues[catCode];
        if (colorVal !== undefined) node.color_value = colorVal;
        nodes.push(node);
      }
    }
  }

  return { nodes };
}

/**
 * Fetch stat card data (pre-computed, no API call needed).
 */
export function fetchStatCardData(spec: ChartSpec): StatCardDataResult {
  const statData = spec.stat_data ?? spec.stats?.[0];
  if (!statData) throw new Error('stat_card requires stat_data or stats[0]');
  return { stat: statData };
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Orchestrator
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch chart data based on chart type.
 * This is the main entry point used by useChartData.
 */
export async function fetchChartData(spec: ChartSpec): Promise<ChartData> {
  switch (spec.type) {
    case 'historical':
    case 'multi_historical':
      return { type: spec.type, data: await fetchHistoricalChartData(spec) };

    case 'time_series':
      return { type: 'time_series', data: await fetchHistoricalChartData(spec) };

    case 'comparison':
      return { type: 'comparison', data: await fetchHistoricalChartData(spec, true) };

    case 'bar':
    case 'distribution': {
      const data = await fetchBarChartData(spec);
      // Sort by value if distribution or sorted flag
      if (spec.type === 'distribution' || spec.sorted) {
        return { type: spec.type, data: sortByValueDescending(data) };
      }
      return { type: spec.type, data };
    }

    case 'scatter':
      return { type: 'scatter', data: await fetchScatterChartData(spec) };

    case 'treemap':
      return { type: 'treemap', data: await fetchTreemapChartData(spec) };

    case 'stat_card':
      return { type: 'stat_card', data: fetchStatCardData(spec) };

    case 'heatmap':
      throw new Error('heatmap chart type is not yet implemented');

    default:
      throw new Error(`Unknown chart type: ${spec.type}`);
  }
}

